import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { joinGroup, resolveInvite, type InviteInfo } from '../lib/db';
import { track } from '../lib/analytics';
import { Icon } from '../components/Icons';
import { Loading, useToast } from '../components/ui';

type State =
  | { kind: 'looking' }
  | { kind: 'found'; invite: InviteInfo }
  | { kind: 'joining' }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

export default function Join() {
  const { code = '' } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const [state, setState] = useState<State>({ kind: 'looking' });

  useEffect(() => {
    let alive = true;
    resolveInvite(code)
      .then((invite) => {
        if (!alive) return;
        setState(invite ? { kind: 'found', invite } : { kind: 'missing' });
      })
      .catch((e: Error) => alive && setState({ kind: 'error', message: e.message }));
    return () => { alive = false; };
  }, [code]);

  async function accept(invite: InviteInfo) {
    if (!user) return;
    setState({ kind: 'joining' });
    try {
      await joinGroup(invite.groupId, user.uid);
      track('trip_joined');
      toast(`You're in — ${invite.groupName}`);
      // Land on Families so the first thing they do is say which household they are.
      nav(`/t/${invite.groupId}/families`, { replace: true });
    } catch (e) {
      setState({ kind: 'error', message: (e as Error).message });
    }
  }

  if (state.kind === 'looking') return <Loading label="Checking that invite…" />;
  if (state.kind === 'joining') return <Loading label="Joining the trip…" />;

  return (
    <div className="signin" style={{ justifyContent: 'center' }}>
      {state.kind === 'found' && (
        <>
          <div className="wordmark" style={{ fontSize: 22 }}>You're invited to</div>
          <div className="mark" style={{ fontSize: 34, marginTop: 6 }}>{state.invite.groupName}</div>
          <p className="tag">
            Join and you'll share one balance with the rest of your household — and see
            exactly what your family owes, or is owed, at any point in the trip.
          </p>
          <button className="btn" style={{ marginTop: 26 }} onClick={() => void accept(state.invite)}>
            Join this trip
          </button>
          <button className="btn ghost" style={{ marginTop: 9 }} onClick={() => nav('/')}>
            Not now
          </button>
        </>
      )}

      {state.kind === 'missing' && (
        <>
          <div className="mark" style={{ fontSize: 30 }}>Invite not found</div>
          <p className="tag">
            This link has expired or been replaced. Ask whoever sent it for a fresh one —
            invite links can be regenerated from the trip's Families tab.
          </p>
          <button className="btn" style={{ marginTop: 24 }} onClick={() => nav('/')}>
            Go to my trips
          </button>
        </>
      )}

      {state.kind === 'error' && (
        <>
          <div className="mark" style={{ fontSize: 30 }}>Couldn't join</div>
          <div className="note" style={{ marginTop: 16, background: 'var(--clay-soft)' }}>
            <Icon name="info" />
            <div>{state.message}</div>
          </div>
          <button className="btn" style={{ marginTop: 20 }} onClick={() => nav('/')}>
            Go to my trips
          </button>
        </>
      )}
    </div>
  );
}
